import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Crown, Gauge, Zap, Youtube, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import Configurator from "@/components/Configurator";
import CentresMap from "@/components/CentresMap";
import { SectionTitle, CTABand } from "@/components/blocks";
import { IMAGES, SERVICES } from "@/data/content";
import { useCentre } from "@/context/CentreContext";
import { useUI } from "@/context/UIContext";

export default function Home() {
  const { centres, setSelectorOpen } = useCentre();
  const { openLead } = useUI();
  const [reals, setReals] = useState([]);

  useEffect(() => { api.get("/realisations").then(({ data }) => setReals(data.slice(0, 3))); }, []);

  return (
    <div data-testid="home-page">
      <Seo title="Horsepower Garage | Réseau national de reprogrammation moteur, Stage 1-2-3 & E85"
        description="Horsepower Garage, réseau national spécialisé en reprogrammation moteur, Stage 1, Stage 2, conversion E85 FlexFuel et banc de puissance. Béziers (maison mère), Montpellier, Perpignan, Marseille, Angers."
        canonical="https://www.horsepowergarage.fr/"
        jsonLd={{ "@context": "https://schema.org", "@type": "AutoRepair", name: "Horsepower Garage",
          description: "Réseau national de reprogrammation moteur et conversion E85", url: "https://www.horsepowergarage.fr/" }} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img src="/photos/hero-beziers.jpg" alt="Porsche 911 GT3 RS devant Horsepower Garage Béziers" className="h-full w-full object-cover object-[70%_center] md:object-right" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-hp-dark via-hp-dark/80 to-hp-dark/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-hp-dark via-transparent to-hp-dark/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full pt-28">
          <Reveal>
            <p className="label-eyebrow mb-4">Réseau national · Reprogrammation moteur</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-display uppercase tracking-tight text-5xl sm:text-6xl md:text-7xl leading-[0.95] max-w-3xl text-white">
              Libérez le <span className="text-hp-yellow">potentiel</span><br />de votre véhicule
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-white/75 text-lg mt-6 max-w-xl leading-relaxed font-light3">
              Reprogrammation moteur, conversion E85 et préparation automobile. Découvrez l'expertise Horsepower
              à travers notre réseau de centres en France.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="flex flex-col sm:flex-row gap-4 mt-9">
              <a href="#configurateur" className="hp-cta px-8 py-4 flex items-center justify-center gap-2 text-sm" data-testid="hero-config">
                <Gauge size={18} /> Configurer mon véhicule
              </a>
              <button onClick={() => setSelectorOpen(true)} className="hp-ghost px-8 py-4 flex items-center justify-center gap-2 text-sm text-white" data-testid="hero-centre">
                <MapPin size={18} /> Trouver mon centre
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RÉSEAU / CHIFFRES */}
      <section className="relative overflow-hidden bg-[#1c1b1e]" data-testid="network-stats">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-hp-yellow/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-28">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-stretch">
            <div className="flex flex-col">
              <SectionTitle eyebrow="Un réseau, une expertise" title="Horsepower, la performance en réseau national"
                sub="Une méthodologie développée dans notre maison mère de Béziers, déployée dans chaque centre du réseau et validée au banc de puissance." />
              <div className="flex flex-wrap gap-2 mt-8" data-testid="network-centres">
                {centres.map((c) => (
                  <Link key={c.slug} to={`/centres/${c.slug}`} className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold border transition-colors ${c.is_mother ? "bg-hp-yellow text-black border-hp-yellow" : "border-white/20 text-white hover:border-hp-yellow hover:text-hp-yellow"}`}>
                    <span className={`h-2 w-2 rounded-full ${c.is_mother ? "bg-black" : "bg-hp-yellow"}`} />{c.city}{c.is_mother && " · Maison mère"}
                  </Link>
                ))}
              </div>
            </div>
            <div data-testid="network-mini-map"><CentresMap height="h-[340px] sm:h-[420px] lg:h-full lg:min-h-[420px]" /></div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-10 mt-16">
            {[
              { n: "6", label: "Centres en France", d: "Béziers, Montpellier, Perpignan, Marseille, Angers, Metz" },
              { n: "10+", label: "Ans d'expertise", d: "Cartographies développées en interne" },
              { n: "5 000+", label: "Véhicules optimisés", d: "Validés au banc de puissance" },
              { n: "9 899", label: "Motorisations au catalogue", d: "Voitures, toutes marques" },
            ].map((st, i) => (
              <Reveal key={st.label} delay={i * 0.08}>
                <div className="border-t-2 border-hp-yellow pt-5">
                  <div className="font-display text-5xl sm:text-6xl xl:text-7xl text-white leading-none tracking-tight">{st.n}</div>
                  <p className="text-white font-bold text-sm mt-3 uppercase tracking-[0.18em]">{st.label}</p>
                  <p className="text-white/50 text-sm mt-1">{st.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONFIGURATEUR */}
      <section id="configurateur" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 scroll-mt-24">
        <SectionTitle eyebrow="Configurateur véhicule" title="Que peut gagner votre véhicule ?"
          sub="Sélectionnez votre véhicule et découvrez instantanément les gains de puissance et de couple selon le stage." />
        <div className="mt-10"><Configurator /></div>
      </section>

      {/* CENTRES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <SectionTitle eyebrow="Nos centres Horsepower" title="Trouvez le centre le plus proche" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {centres.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.08}>
              <div className="hp-card group overflow-hidden h-full flex flex-col" data-testid={`home-centre-${c.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  {c.is_mother && (
                    <span className="absolute left-4 top-4 flex items-center gap-1 bg-hp-yellow text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1">
                      <Crown size={12} /> Maison mère
                    </span>
                  )}
                  <h3 className="absolute bottom-4 left-4 font-display font-black text-2xl uppercase tracking-tight">{c.city}</h3>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="space-y-1.5 text-sm text-neutral-400 flex-1">
                    {(c.address || c.postal_city) && <p className="flex items-start gap-2"><MapPin size={15} className="text-hp-yellow mt-0.5 shrink-0" />{[c.address, c.postal_city].filter(Boolean).join(", ")}</p>}
                    <p className="flex items-center gap-2"><Phone size={15} className="text-hp-yellow shrink-0" />{c.phone}</p>
                  </div>
                  <div className="flex gap-2 mt-5">
                    <Link to={`/centres/${c.slug}`} className="hp-ghost flex-1 text-center text-xs px-3 py-2.5" data-testid={`home-centre-link-${c.slug}`}>Découvrir</Link>
                    <button onClick={() => openLead({ request_type: "rdv", centre_slug: c.slug })} className="hp-cta flex-1 text-xs px-3 py-2.5" data-testid={`home-centre-rdv-${c.slug}`}>Rendez-vous</button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <SectionTitle eyebrow="Nos services" title="Une expertise complète" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.06}>
              <Link to={s.path} className="hp-card group flex flex-col overflow-hidden h-full" data-testid={`home-service-${s.slug}`}>
                <div className="overflow-hidden bg-black aspect-[4/5]">
                  <img src={s.image} alt={s.name} className={`h-full w-full ${s.fit === "cover" ? "object-cover" : "object-contain"} transition-transform duration-500 group-hover:scale-[1.03]`} loading="lazy" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display font-bold uppercase tracking-tight text-lg group-hover:text-hp-yellow transition-colors">{s.name}</h3>
                  <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{s.desc}</p>
                  <span className="inline-flex items-center gap-1 text-hp-yellow text-xs font-bold uppercase tracking-wide mt-auto pt-4">En savoir plus <ArrowRight size={14} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* E85 HIGHLIGHT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src="/photos/e85-highlight.jpg" alt="Conversion E85 FlexFuel" className="h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="label-eyebrow mb-3"><Zap size={13} className="inline mr-1" /> Éco-performance</p>
            <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl leading-tight">Conversion E85 FlexFuel</h2>
            <p className="text-neutral-400 mt-4 leading-relaxed">
              Économisez sur votre budget carburant grâce à la conversion éthanol E85, tout en préservant les performances
              de votre moteur. Un biocarburant renouvelable, une intervention sur calculateur maîtrisée.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Link to="/services/e85" className="hp-cta px-6 py-3.5 text-sm inline-flex items-center justify-center gap-2" data-testid="home-e85-link">Découvrir l'E85 <ArrowRight size={16} /></Link>
              <Link to="/services/e85#simulateur" className="hp-ghost px-6 py-3.5 text-sm inline-flex items-center justify-center">Simuler mon économie</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REALISATIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <SectionTitle eyebrow="Nos réalisations" title="Ils roulent Horsepower" />
          <Link to="/realisations" className="hp-ghost px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="home-reals-all">Voir tout <ArrowRight size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 mt-12">
          {reals.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.08}>
              <div className="hp-card group overflow-hidden">
                <div className="relative h-72 overflow-hidden">
                  <img src={r.image.startsWith("/photos/") ? r.image.replace(".webp", "-sm.webp") : r.image} alt={`${r.vehicle} — Horsepower Garage`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <span className="absolute left-3 top-3 bg-hp-yellow text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1">{r.category}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold uppercase tracking-tight">{r.vehicle}</h3>
                  {r.origin_hp && r.final_hp ? (
                    <div className="flex items-center gap-4 mt-3 font-stat">
                      <span className="text-neutral-400 text-lg">{r.origin_hp} CH</span>
                      <ArrowRight size={16} className="text-hp-yellow" />
                      <span className="text-hp-yellow font-black text-2xl">{r.final_hp} CH</span>
                    </div>
                  ) : <p className="text-sm text-neutral-400 mt-2">{r.modification}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BOUTIQUE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="home-shop">
        <div className="hp-card overflow-hidden grid lg:grid-cols-[1fr_1.2fr]">
          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <p className="text-hp-yellow text-xs tracking-[0.3em] uppercase font-bold inline-flex items-center gap-2"><ShoppingBag size={14} /> Boutique officielle</p>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-4">Portez les couleurs Horsepower</h2>
            <p className="text-neutral-400 mt-4">Sweats, vestes softshell, casquettes, goodies… la collection officielle du réseau, brodée à Béziers. Retrait gratuit dans votre centre ou livraison à domicile.</p>
            <Link to="/boutique" className="hp-cta inline-flex items-center gap-2 px-8 py-4 mt-8 text-sm w-fit" data-testid="home-shop-cta">Découvrir la boutique <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-3 gap-px bg-white/5">
            {["sweat-capuche", "veste-gal-pad", "casquette-5-pans", "tshirt-coton", "desodorisant", "veste-softshell"].map((s) => (
              <Link key={s} to={`/boutique/${s}`} className="bg-white aspect-square overflow-hidden group"><img src={`/shop/${s}.jpg`} alt="" className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-110" loading="lazy" /></Link>
            ))}
          </div>
        </div>
      </section>

      {/* HORSEPOWER TV */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <a href="https://www.youtube.com/@HorsePowerGarage/videos" target="_blank" rel="noopener noreferrer"
          className="group relative block overflow-hidden rounded" data-testid="home-youtube">
          <div className="relative h-[340px] sm:h-[420px]">
            <img src={IMAGES.sportsDark} alt="Horsepower TV" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-hp-dark via-hp-dark/50 to-hp-dark/20" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <span className="grid place-items-center h-20 w-20 rounded-full bg-hp-yellow text-hp-dark mb-6 transition-transform group-hover:scale-110">
                <Youtube size={38} />
              </span>
              <p className="label-eyebrow mb-2">Horsepower TV</p>
              <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl text-white max-w-2xl">Découvrez nos réalisations en vidéo</h2>
              <p className="text-white/70 mt-3 max-w-xl font-light3">Passages au banc, préparations moteur et coulisses de nos centres sur notre chaîne YouTube.</p>
              <span className="hp-cta mt-6 px-7 py-3.5 text-sm inline-flex items-center gap-2">Voir la chaîne <ArrowRight size={16} /></span>
            </div>
          </div>
        </a>
      </section>

      <CTABand />
    </div>
  );
}
